import {useRouteMatches} from '@github-ui/react-core/future/query-route'
import {useChildRouteQuery, useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useCurrentQueryRoute} from '@github-ui/react-core/future/use-current-query-route'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {GLOBAL_SIDEBAR_STORAGE_KEY} from '@github-ui/layout-primitives/use-collapsible-sidebar-state'
import {useQueryClient, type QueryKey} from '@github-ui/react-query'
import {Outlet, useParams, useSearchParams} from '@github-ui/react-router'
import {addGitHubToTitle, setTitle} from '@github-ui/document-metadata'
import type {WorkflowRunsRoutePayload} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import type {CachesIndexPayload} from '@github-ui/dotcom-schema/types/actions/v1/caches_index'
import {ActionsPageHeaderSchema} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {useCallback, useEffect, type ComponentProps, type ReactNode} from 'react'
import {ActionsPageHeader} from '../components/ActionsPageHeader'
import {RailsSidebarTriggerBridge} from '../components/RailsSidebarTriggerBridge'
import {WorkflowSidebar} from '../components/WorkflowSidebar'
import {getActionsNotEnabledBanner, useActionsNotEnabledBanner} from '../components/Banners/ActionsNotEnabledBanner'
import {getActionsSurveyBanner, useActionsSurvey} from '../components/Banners/ActionsSurvey'
import {getBillingCriticalBanner, useBillingCriticalBanner} from '../components/Banners/BillingCriticalBanner'
import {getBillingWarningBanner, useBillingWarningBanner} from '../components/Banners/BillingWarningBanner'
import {getDisabledWorkflowBanner, useDisabledWorkflowBanner} from '../components/Banners/DisabledWorkflowBanner'
import {getInvocationBlockedBanner, useInvocationBlockedBanner} from '../components/Banners/InvocationBlockedBanner'
import {
  getRepositoryRateLimitBanner,
  useRepositoryRateLimitBanner,
} from '../components/Banners/RepositoryRateLimitBanner'
import {getRunnerCapacityBanner, useRunnerCapacityBanner} from '../components/Banners/RunnerCapacityBanner'
import {
  getStatelessS2sTokenFormatChangeBanner,
  useStatelessS2sTokenFormatChangeBanner,
} from '../components/Banners/StatelessS2sTokenFormatChangeBanner'
import {WorkflowSidebarFooter} from '../components/WorkflowSidebarFooter'
import {useWorkflowDisable} from '../hooks/use-workflow-disable'
import {useWorkflowPinning} from '../hooks/use-workflow-pinning'
import {useWorkflowSidebarState} from '../hooks/use-workflow-sidebar-state'
import type {WorkflowSidebarState} from '../hooks/use-workflow-sidebar-state'
import {workflowSidebarQueryKeyPrefix} from '../hooks/use-workflow-pagination'
import {useAnalytics} from '@github-ui/use-analytics'
import {workflowRunsRoute} from './workflow-runs-route'
import {type WorkflowsLayoutPayload, workflowsLayoutRoute} from './workflows-layout-route'
import {workflowRouteParam} from '../workflow-path'
import {useActionsAnalytics} from '../hooks/use-actions-analytics'
import type {ActionsShellRouteHandle} from './actions-shell-route-handle'

interface WorkflowsLayoutBannersProps {
  actionsNotEnabledBanner: Parameters<typeof useActionsNotEnabledBanner>[0]
  actionsSurvey: Parameters<typeof useActionsSurvey>[0]
  billingCriticalBanner: Parameters<typeof useBillingCriticalBanner>[0]
  billingWarningBanner: Parameters<typeof useBillingWarningBanner>[0]
  disabledWorkflowBanner: Parameters<typeof useDisabledWorkflowBanner>[0]
  invocationBlockedBanner: Parameters<typeof useInvocationBlockedBanner>[0]
  repositoryRateLimitBanner: Parameters<typeof useRepositoryRateLimitBanner>[0]
  runnerCapacityBanner: Parameters<typeof useRunnerCapacityBanner>[0]
  statelessS2sTokenFormatChangeBanner: Parameters<typeof useStatelessS2sTokenFormatChangeBanner>[0]
}

function WorkflowsLayoutBanners({
  actionsNotEnabledBanner,
  actionsSurvey,
  billingCriticalBanner,
  billingWarningBanner,
  disabledWorkflowBanner,
  invocationBlockedBanner,
  repositoryRateLimitBanner,
  runnerCapacityBanner,
  statelessS2sTokenFormatChangeBanner,
}: WorkflowsLayoutBannersProps) {
  useActionsNotEnabledBanner(actionsNotEnabledBanner)
  useActionsSurvey(actionsSurvey)
  useBillingWarningBanner(billingWarningBanner)
  useBillingCriticalBanner(billingCriticalBanner)
  useDisabledWorkflowBanner(disabledWorkflowBanner)
  useInvocationBlockedBanner(invocationBlockedBanner)
  useRepositoryRateLimitBanner(repositoryRateLimitBanner)
  useRunnerCapacityBanner(runnerCapacityBanner)
  useStatelessS2sTokenFormatChangeBanner(statelessS2sTokenFormatChangeBanner)
  return null
}

function ActionsShellBanners({
  statelessS2sTokenFormatChangeBanner,
}: Pick<WorkflowsLayoutBannersProps, 'statelessS2sTokenFormatChangeBanner'>) {
  useStatelessS2sTokenFormatChangeBanner(statelessS2sTokenFormatChangeBanner)
  return null
}

type WorkflowsLayoutCacheData = {
  payload: WorkflowsLayoutPayload
}

interface WorkflowsLayoutViewProps extends Omit<ComponentProps<typeof WorkflowSidebar>, 'state'> {
  header?: ReactNode
  title: string
  state: WorkflowSidebarState
  canCreateWorkflow: boolean
  showDeployments: boolean
  showAttestations: boolean
  showRunners: boolean
  lockfilePath?: string | null
  showUsageMetrics: boolean
  children: ReactNode
  storageKey?: string
  initialExpanded?: boolean
  defaultExpanded?: boolean
  headerOwnedByChild?: boolean
  showNewWorkflowAction?: boolean
  bannerProviderProps?: ComponentProps<typeof FilteredListPageLayout>['bannerProviderProps']
}

interface WorkflowPageHeaderProps extends ComponentProps<typeof ActionsPageHeader> {
  queryKey: QueryKey
}

function WorkflowPageHeader({owner, repo, queryKey, ...props}: WorkflowPageHeaderProps) {
  const {disableWorkflow} = useWorkflowDisable({owner, repo, queryKey})
  return <ActionsPageHeader owner={owner} repo={repo} onDisableWorkflow={disableWorkflow} {...props} />
}

export function WorkflowsLayoutView({
  header,
  title,
  state,
  canCreateWorkflow,
  showOnlyRequiredWorkflows,
  showDeployments,
  showAttestations,
  showRunners,
  lockfilePath,
  showUsageMetrics,
  children,
  storageKey,
  initialExpanded,
  defaultExpanded,
  headerOwnedByChild = false,
  showNewWorkflowAction = true,
  bannerProviderProps,
  ...sidebarProps
}: WorkflowsLayoutViewProps) {
  const track = useActionsAnalytics()
  return (
    <FilteredListPageLayout
      storageKey={storageKey}
      initialExpanded={initialExpanded}
      defaultExpanded={defaultExpanded}
      fitViewport
      bannerProviderProps={bannerProviderProps}
    >
      {headerOwnedByChild ? null : (
        <FilteredListPageLayout.Header>
          {header ?? (
            <FilteredListPageLayout.PageHeader>
              <FilteredListPageLayout.PageHeader.TitleArea>
                <FilteredListPageLayout.PageHeader.Title as="h1">{title}</FilteredListPageLayout.PageHeader.Title>
              </FilteredListPageLayout.PageHeader.TitleArea>
              {canCreateWorkflow && showNewWorkflowAction ? (
                <FilteredListPageLayout.PageHeader.Actions>
                  <FilteredListPageLayout.HeaderActions
                    actions={[
                      {
                        key: 'new-workflow',
                        label: 'New workflow',
                        primary: false,
                        href: `/${sidebarProps.owner}/${sidebarProps.repo}/actions/new`,
                        onClick: () =>
                          track('workflow_header.new_workflow', 'ref_cta:new_workflow;ref_loc:actions_workflow_header'),
                      },
                    ]}
                  />
                </FilteredListPageLayout.PageHeader.Actions>
              ) : null}
            </FilteredListPageLayout.PageHeader>
          )}
        </FilteredListPageLayout.Header>
      )}
      <FilteredListPageLayout.Sidebar
        aria-label="Actions"
        dialogTitle="Actions"
        scrollable
        expandedWidth="large"
        dialogWidth="large"
        footer={
          showOnlyRequiredWorkflows ? undefined : (
            <WorkflowSidebarFooter
              owner={sidebarProps.owner}
              repo={sidebarProps.repo}
              softNavigationEnabled={sidebarProps.softNavigationEnabled}
              showDeployments={showDeployments}
              showAttestations={showAttestations}
              showRunners={showRunners}
              lockfilePath={lockfilePath}
              showUsageMetrics={showUsageMetrics}
              selection={sidebarProps.selection}
            />
          )
        }
      >
        <WorkflowSidebar {...sidebarProps} state={state} showOnlyRequiredWorkflows={showOnlyRequiredWorkflows} />
      </FilteredListPageLayout.Sidebar>
      <FilteredListPageLayout.Content as="div">
        {headerOwnedByChild ? <RailsSidebarTriggerBridge /> : null}
        {children}
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}

export function WorkflowsLayout() {
  const {data: payload, queryKey, refetch} = useRouteQuery(workflowsLayoutRoute, 'mainQuery')
  const queryClient = useQueryClient()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const currentChildRoute = useCurrentQueryRoute()
  const owner = params.owner ?? ''
  const repo = params.repo ?? ''
  const sidebarState = useWorkflowSidebarState({
    initialWorkflows: payload.workflows,
    totalCount: payload.totalCount,
    owner,
    repo,
    selectedWorkflow: payload.selectedWorkflow,
  })
  const matches = useRouteMatches()
  const routeHandle = [...matches]
    .reverse()
    .map(match => (match.handle as {actionsShell?: ActionsShellRouteHandle} | undefined)?.actionsShell)
    .find(handle => handle !== undefined)
  const currentChildQueryName = routeHandle?.kind === 'rails-fragment' ? `${currentChildRoute.id}.Main` : 'mainQuery'
  const {
    data: currentChildPayload,
    isPlaceholderData: isChildPayloadPlaceholder,
    queryKey: currentChildQueryKey,
  } = useChildRouteQuery(currentChildRoute, currentChildQueryName, {placeholderData: previousData => previousData})
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const refreshWorkflows = useCallback(async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({queryKey: workflowSidebarQueryKeyPrefix(owner, repo)}),
    ])
  }, [owner, queryClient, refetch, repo])
  const {pinWorkflow, unpinWorkflow} = useWorkflowPinning({owner, repo, refreshWorkflows})

  if (!routeHandle) throw new Error('Actions routes must define an actionsShell route handle')

  const isWorkflowRoute = routeHandle.kind === 'workflow'
  const selection = routeHandle.getSelection(params)
  const isNativeRoute = routeHandle.kind === 'native'
  const isNativeRunnersRoute = isNativeRoute && selection.type === 'runners'
  const cachesPayload =
    isNativeRoute && selection.type === 'caches' ? (currentChildPayload as CachesIndexPayload | undefined) : undefined
  const isWorkflowRunsRoute = currentChildRoute.id === workflowRunsRoute.id
  if (isWorkflowRoute && !currentChildQueryKey) {
    throw new Error('Workflow routes must define a child query key')
  }
  const workflowQueryKey = currentChildQueryKey ?? queryKey
  const leafPayload = isWorkflowRoute ? (currentChildPayload as WorkflowRunsRoutePayload | undefined) : undefined
  const disabledWorkflowBanner = isChildPayloadPlaceholder ? undefined : leafPayload?.disabledWorkflowBanner
  const invocationBlockedBanner = leafPayload ? getInvocationBlockedBanner(leafPayload) : undefined
  const clearStatelessS2sTokenFormatChangeBanner = useCallback(() => {
    queryClient.setQueryData<WorkflowsLayoutCacheData>(queryKey, oldData => {
      if (!oldData) return oldData
      return {
        ...oldData,
        payload: {
          ...oldData.payload,
          banners: {
            ...oldData.payload.banners,
            statelessS2sTokenFormatChangeBanner: undefined,
          },
        },
      }
    })
  }, [queryClient, queryKey])
  const statelessS2sTokenFormatChangeBanner = payload.banners.statelessS2sTokenFormatChangeBanner?.dismissPath
    ? getStatelessS2sTokenFormatChangeBanner({
        ...payload.banners.statelessS2sTokenFormatChangeBanner,
        sendAnalyticsEvent,
        onDismissed: clearStatelessS2sTokenFormatChangeBanner,
      })
    : undefined
  const bannerProviderProps = {
    initialBanners: [
      ...(isWorkflowRoute
        ? [
            ...(invocationBlockedBanner ? [invocationBlockedBanner] : []),
            ...(payload.banners.billingWarningBanner
              ? [getBillingWarningBanner(payload.banners.billingWarningBanner)]
              : []),
            ...(payload.banners.billingCriticalBanner
              ? [getBillingCriticalBanner(payload.banners.billingCriticalBanner)]
              : []),
            ...(payload.showActionsNotEnabledBanner
              ? [
                  getActionsNotEnabledBanner({
                    isFork: payload.isFork,
                    enablePath: payload.banners.enablePath,
                    workflowsPath: payload.workflowsPath,
                    queryKey,
                  }),
                ]
              : []),
            ...(payload.banners.actionsSurvey ? [getActionsSurveyBanner(payload.banners.actionsSurvey)] : []),
            ...(disabledWorkflowBanner
              ? [
                  getDisabledWorkflowBanner({
                    ...disabledWorkflowBanner,
                    queryKey: workflowQueryKey,
                    owner,
                    repo,
                  }),
                ]
              : []),
            ...(payload.banners.repositoryRateLimitBanner
              ? [getRepositoryRateLimitBanner(payload.banners.repositoryRateLimitBanner)]
              : []),
            ...(payload.banners.runnerCapacityBanner
              ? [getRunnerCapacityBanner(payload.banners.runnerCapacityBanner)]
              : []),
          ]
        : []),
      ...(routeHandle.kind !== 'rails-fragment' && statelessS2sTokenFormatChangeBanner
        ? [statelessS2sTokenFormatChangeBanner]
        : []),
    ],
  }
  const title = leafPayload?.title ?? routeHandle.title
  const showPageHeader = !isWorkflowRunsRoute || leafPayload?.workflowExists !== false
  const showOnlyRequiredWorkflows = payload.showOnlyRequiredWorkflows ?? false
  const search = searchParams.toString()

  useEffect(() => {
    // Wait for TitleManager to apply the cached title, then replace it with the child route's.
    const timeoutId = setTimeout(() => setTitle(addGitHubToTitle(title)), 0)
    return () => clearTimeout(timeoutId)
  }, [search, title])

  useEffect(() => {
    if (isNativeRunnersRoute && payload.runnersSetupPath) {
      sendAnalyticsEvent('view_rendered', undefined, {name: 'repository_runners/new_runner_cta'})
    }
  }, [isNativeRunnersRoute, payload.runnersSetupPath, sendAnalyticsEvent])

  return (
    <WorkflowsLayoutView
      header={
        isNativeRunnersRoute ? (
          <FilteredListPageLayout.PageHeader>
            <FilteredListPageLayout.PageHeader.TitleArea>
              <FilteredListPageLayout.PageHeader.Title as="h1">Runners</FilteredListPageLayout.PageHeader.Title>
            </FilteredListPageLayout.PageHeader.TitleArea>
            {payload.runnersSetupPath ? (
              <FilteredListPageLayout.PageHeader.Actions>
                <FilteredListPageLayout.HeaderActions
                  actions={[
                    {
                      key: 'new-runner',
                      label: 'New runner',
                      primary: true,
                      href: payload.runnersSetupPath,
                      onClick: () =>
                        sendAnalyticsEvent('analytics.click', undefined, {
                          category: 'repository_runners',
                          action: 'click_new_runner_cta',
                          label: `ref_cta:new_runner;ref_loc:repository_runner_header;path:${payload.runnersSetupPath}`,
                        }),
                    },
                  ]}
                />
              </FilteredListPageLayout.PageHeader.Actions>
            ) : null}
          </FilteredListPageLayout.PageHeader>
        ) : isWorkflowRoute && leafPayload && showPageHeader ? (
          <WorkflowPageHeader
            header={leafPayload.header}
            owner={owner}
            repo={repo}
            queryKey={queryKey}
            canCreateWorkflow={payload.canCreateWorkflow && !isWorkflowRunsRoute}
            workflowDispatchAvailable={!isChildPayloadPlaceholder && leafPayload.workflowDispatchAvailable}
            allowPinning={payload.allowPinning && !isChildPayloadPlaceholder}
            workflowFilename={isChildPayloadPlaceholder ? undefined : workflowRouteParam(params)}
            statusBadgeLab={searchParams.get('lab') === 'true'}
            pinnedWorkflowIds={payload.pinnedWorkflowIds}
            onPinWorkflow={isChildPayloadPlaceholder ? undefined : pinWorkflow}
            onUnpinWorkflow={isChildPayloadPlaceholder ? undefined : unpinWorkflow}
          />
        ) : cachesPayload ? (
          <ActionsPageHeader
            header={create(ActionsPageHeaderSchema, {
              title: 'Caches',
              descriptionText: 'Showing caches from all workflows. ',
              descriptionLinkText: 'Learn more about managing caches.',
              descriptionLinkUrl: cachesPayload.cachingHelpUrl,
            })}
            owner={owner}
            repo={repo}
            canCreateWorkflow={false}
          />
        ) : undefined
      }
      headerOwnedByChild={routeHandle.kind === 'rails-fragment'}
      showNewWorkflowAction={isWorkflowRoute}
      title={title}
      state={sidebarState}
      pinnedWorkflowIds={payload.pinnedWorkflowIds}
      allowPinning={payload.allowPinning}
      // The Rails runners fragment hosts a nested React root; navigate natively to avoid tearing it down.
      softNavigationEnabled={selection.type !== 'runners' || isNativeRoute}
      owner={owner}
      repo={repo}
      selection={selection}
      canCreateWorkflow={payload.canCreateWorkflow && !isWorkflowRunsRoute}
      showOnlyRequiredWorkflows={showOnlyRequiredWorkflows}
      showDeployments={payload.showDeployments}
      showAttestations={payload.showAttestations}
      showRunners={payload.showRunners}
      lockfilePath={payload.lockfilePath}
      showUsageMetrics={payload.showUsageMetrics}
      refreshWorkflows={refreshWorkflows}
      storageKey={GLOBAL_SIDEBAR_STORAGE_KEY}
      initialExpanded={payload.sidebarExpanded ?? undefined}
      bannerProviderProps={bannerProviderProps}
    >
      {isWorkflowRoute ? (
        <WorkflowsLayoutBanners
          actionsNotEnabledBanner={{
            showBanner: payload.showActionsNotEnabledBanner,
            isFork: payload.isFork,
            enablePath: payload.banners.enablePath,
            workflowsPath: payload.workflowsPath,
            queryKey,
          }}
          actionsSurvey={{data: payload.banners.actionsSurvey}}
          billingWarningBanner={{data: payload.banners.billingWarningBanner}}
          billingCriticalBanner={{data: payload.banners.billingCriticalBanner}}
          disabledWorkflowBanner={{
            data: disabledWorkflowBanner,
            queryKey: workflowQueryKey,
            owner,
            repo,
          }}
          invocationBlockedBanner={{
            repoBlocked: leafPayload?.repoBlocked ?? false,
            userBlocked: leafPayload?.userBlocked ?? false,
          }}
          repositoryRateLimitBanner={{data: payload.banners.repositoryRateLimitBanner}}
          runnerCapacityBanner={{data: payload.banners.runnerCapacityBanner}}
          statelessS2sTokenFormatChangeBanner={{
            data: payload.banners.statelessS2sTokenFormatChangeBanner,
            onDismissed: clearStatelessS2sTokenFormatChangeBanner,
          }}
        />
      ) : isNativeRoute ? (
        <ActionsShellBanners
          statelessS2sTokenFormatChangeBanner={{
            data: payload.banners.statelessS2sTokenFormatChangeBanner,
            onDismissed: clearStatelessS2sTokenFormatChangeBanner,
          }}
        />
      ) : null}
      <Outlet />
    </WorkflowsLayoutView>
  )
}
